/**
 * AthleteEditorModal — parent self-serve add / edit one athlete on the dashboard.
 *
 * Field parity with ProfileWizard step 3:
 *   first_name, last_name, dob, gender, track, grade, sports[], sports_other,
 *   health_notes, email
 *
 * Modes:
 *   mode="add"  → POST /api/profile/athletes
 *   mode="edit" → PATCH /api/profile/athletes/{athlete_id}
 *
 * On success calls onSaved() which the dashboard uses to refresh the user
 * (so the new/edited athlete card shows up immediately).
 */
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Loader2, Check, Trash2, AlertCircle } from "lucide-react";
import { GRADE_OPTIONS } from "@/lib/athleteTaxonomy";
import SportsPicker from "@/components/SportsPicker";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TRACKS = [
    { value: "school_foundational", label: "Foundational Athleticism (8–10)", hint: "Movement literacy & coordination" },
    { value: "school_elite_youth", label: "Elite Youth Performance (11–14)", hint: "Capacity & force introduction" },
    { value: "school_elite_peak", label: "Elite Peak Performance (14–18)", hint: "Sport-specific peaking" },
    { value: "pursuing_pro", label: "Pursuing Pro (18–35)", hint: "Elite performance" },
    { value: "athletic_aesthetic", label: "Adult Performance", hint: "Working professionals — look better, feel better, perform better" },
];

const CHILD_TRACKS = new Set(["school_foundational", "school_elite_youth", "school_elite_peak"]);
const isChildTrack = (t) => CHILD_TRACKS.has(t);

function suggestTrackForAge(dobStr) {
    if (!dobStr) return null;
    const dob = new Date(dobStr);
    if (Number.isNaN(dob.getTime())) return null;
    const today = new Date();
    let age = today.getFullYear() - dob.getFullYear();
    const m = today.getMonth() - dob.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) age--;
    if (age < 8) return null;
    if (age <= 10) return "school_foundational";
    if (age <= 14) return "school_elite_youth";
    if (age <= 18) return "school_elite_peak";
    if (age <= 35) return "pursuing_pro";
    return "athletic_aesthetic";
}

const EMPTY = {
    first_name: "",
    last_name: "",
    dob: "",
    gender: "",
    track: "school_elite_youth",
    grade: "",
    sports: [],
    sports_other: "",
    health_notes: "",
    email: "",
    phone: "",
};

export default function AthleteEditorModal({ mode = "add", athlete, onClose, onSaved, clientUserId = null }) {
    const initial = athlete ? { ...EMPTY, ...athlete } : { ...EMPTY };
    const [form, setForm] = useState(initial);
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [error, setError] = useState(null);

    // When clientUserId is set, we're a coach/owner editing a client's
    // athletes (coach console). Otherwise we're the parent editing our own.
    const basePath = clientUserId
        ? `${API}/coach/clients/${clientUserId}/athletes`
        : `${API}/profile/athletes`;

    const set = (patch) => setForm((f) => ({ ...f, ...patch }));

    const validate = () => {
        if (!form.first_name.trim() || !form.last_name.trim()) return "First and last name are required.";
        if (!form.dob) return "Date of birth is required.";
        if (!form.track) return "Pick a track.";
        return null;
    };

    const save = async () => {
        const v = validate();
        if (v) {
            setError(v);
            return;
        }
        setError(null);
        setSaving(true);
        try {
            const body = {
                first_name: form.first_name.trim(),
                last_name: form.last_name.trim(),
                dob: form.dob,
                gender: form.gender || null,
                track: form.track,
                grade: form.grade || null,
                sports: form.sports || [],
                sports_other: (form.sports_other || "").trim() || null,
                health_notes: (form.health_notes || "").trim(),
                email: (form.email || "").trim() || null,
                phone: (form.phone || "").trim() || null,
            };
            if (mode === "add") {
                await axios.post(basePath, body, { withCredentials: true });
                toast.success("Athlete added");
            } else {
                await axios.patch(
                    `${basePath}/${athlete.athlete_id}`, body,
                    { withCredentials: true },
                );
                toast.success("Athlete updated");
            }
            onSaved?.();
            onClose();
        } catch (e) {
            const detail = e?.response?.data?.detail || "Could not save athlete.";
            setError(typeof detail === "string" ? detail : "Validation error.");
            toast.error("Save failed", { description: typeof detail === "string" ? detail : null });
        } finally {
            setSaving(false);
        }
    };

    const remove = async () => {
        if (!athlete) return;
        if (!window.confirm(`Remove ${athlete.first_name} ${athlete.last_name} from your profile? This can't be undone.`)) return;
        setDeleting(true);
        setError(null);
        try {
            const r = await axios.delete(`${basePath}/${athlete.athlete_id}`, { withCredentials: true });
            const cancelled = r.data?.cancelled_bookings || 0;
            toast.success("Athlete removed", cancelled > 0 ? { description: `${cancelled} upcoming booking(s) were cancelled.` } : undefined);
            onSaved?.();
            onClose();
        } catch (e) {
            const detail = e?.response?.data?.detail || "Could not remove athlete.";
            const status = e?.response?.status;
            // 400 with the "upcoming session(s)" guard → offer cascade-delete in one extra confirm.
            const isBookingGuard = status === 400 && typeof detail === "string" && /upcoming session/i.test(detail);
            if (isBookingGuard) {
                const proceed = window.confirm(`${detail}\n\nClick OK to cancel those sessions AND remove the athlete now. Cancel to keep both.`);
                if (proceed) {
                    try {
                        const r2 = await axios.delete(`${basePath}/${athlete.athlete_id}?force=true`, { withCredentials: true });
                        const cancelled = r2.data?.cancelled_bookings || 0;
                        toast.success("Athlete removed", { description: `${cancelled} upcoming booking(s) were cancelled.` });
                        onSaved?.();
                        onClose();
                        return;
                    } catch (e2) {
                        const d2 = e2?.response?.data?.detail || "Couldn't remove athlete on retry.";
                        setError(typeof d2 === "string" ? d2 : "Retry failed.");
                        toast.error("Remove failed", { description: typeof d2 === "string" ? d2 : null });
                        return;
                    }
                }
                // User backed out — no-op
                setError(null);
                return;
            }
            setError(typeof detail === "string" ? detail : "Couldn't remove athlete.");
            toast.error("Remove failed", { description: typeof detail === "string" ? detail : null });
        } finally {
            setDeleting(false);
        }
    };

    const suggested = suggestTrackForAge(form.dob);
    const showSuggestion = suggested && suggested !== form.track;

    return (
        <div className="fixed inset-0 z-[70] bg-black/85 backdrop-blur-sm overflow-y-auto">
            <div className="min-h-full flex items-start sm:items-center justify-center p-4">
                <div
                    data-testid="athlete-editor-modal"
                    className="bg-zinc-950 border border-zinc-800 rounded-2xl w-full max-w-2xl my-6 max-h-[calc(100vh-3rem)] overflow-y-auto"
                >
                    <div className="flex justify-between items-center px-6 pt-5 pb-3 sticky top-0 bg-zinc-950/95 backdrop-blur z-10 border-b border-zinc-900">
                        <h3 className="font-display text-2xl tracking-wider text-white uppercase">
                            {mode === "add" ? "Add an athlete" : "Edit athlete"}
                        </h3>
                        <button
                            type="button"
                            onClick={onClose}
                            data-testid="athlete-editor-close"
                            className="text-zinc-300 hover:text-white"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    <div className="px-6 py-6 space-y-5">
                        <div className="grid sm:grid-cols-2 gap-4">
                            <FieldLabel label="First name" required>
                                <Input
                                    value={form.first_name}
                                    onChange={(v) => set({ first_name: v })}
                                    testid="ae-first-name"
                                />
                            </FieldLabel>
                            <FieldLabel label="Last name" required>
                                <Input
                                    value={form.last_name}
                                    onChange={(v) => set({ last_name: v })}
                                    testid="ae-last-name"
                                />
                            </FieldLabel>
                            <FieldLabel label="Email (optional)">
                                <Input
                                    type="email"
                                    value={form.email || ""}
                                    onChange={(v) => set({ email: v })}
                                    placeholder="For older athletes who prefer their own email"
                                    testid="ae-email"
                                />
                            </FieldLabel>
                            <FieldLabel label="Phone (optional)">
                                <Input
                                    type="tel"
                                    value={form.phone || ""}
                                    onChange={(v) => set({ phone: v })}
                                    placeholder="(555) 555-5555"
                                    testid="ae-phone"
                                />
                            </FieldLabel>
                            <FieldLabel label="Date of birth" required>
                                <Input
                                    type="date"
                                    value={form.dob || ""}
                                    onChange={(v) => {
                                        const sug = suggestTrackForAge(v);
                                        set({ dob: v, track: sug || form.track });
                                    }}
                                    testid="ae-dob"
                                />
                            </FieldLabel>
                            <FieldLabel label="Gender">
                                <select
                                    value={form.gender || ""}
                                    onChange={(e) => set({ gender: e.target.value })}
                                    data-testid="ae-gender"
                                    className={inputCls}
                                >
                                    <option value="">Select…</option>
                                    <option value="male">Male</option>
                                    <option value="female">Female</option>
                                    <option value="other">Other</option>
                                    <option value="prefer_not_to_say">Prefer not to say</option>
                                </select>
                            </FieldLabel>
                            <FieldLabel label="Track" required span={2}>
                                <select
                                    value={form.track}
                                    onChange={(e) => set({ track: e.target.value })}
                                    data-testid="ae-track"
                                    className={inputCls}
                                >
                                    {TRACKS.map((t) => (
                                        <option key={t.value} value={t.value}>
                                            {t.label} — {t.hint}
                                        </option>
                                    ))}
                                </select>
                                {showSuggestion && (
                                    <div className="text-[11px] text-zinc-300 mt-2">
                                        Suggested for age:{" "}
                                        <span className="text-brand-red">
                                            {TRACKS.find((t) => t.value === suggested)?.label}
                                        </span>
                                    </div>
                                )}
                            </FieldLabel>
                            {isChildTrack(form.track) && (
                                <FieldLabel label="Grade" span={2}>
                                    <select
                                        value={form.grade || ""}
                                        onChange={(e) => set({ grade: e.target.value })}
                                        data-testid="ae-grade"
                                        className={inputCls}
                                    >
                                        <option value="">Select grade…</option>
                                        {GRADE_OPTIONS.map((g) => (
                                            <option key={g.value} value={g.value}>{g.label}</option>
                                        ))}
                                    </select>
                                </FieldLabel>
                            )}
                            <FieldLabel label="Sport(s) — pick all that apply" span={2}>
                                <SportsPicker
                                    value={form.sports || []}
                                    other={form.sports_other || ""}
                                    onChange={(sports, sports_other) => set({ sports, sports_other })}
                                    testidPrefix="ae-sports"
                                />
                            </FieldLabel>
                            <FieldLabel label="Injuries / health notes (optional)" span={2}>
                                <textarea
                                    rows={2}
                                    value={form.health_notes}
                                    onChange={(e) => set({ health_notes: e.target.value })}
                                    placeholder="Anything we should know — past injuries, conditions, doctor notes."
                                    data-testid="ae-health-notes"
                                    className={inputCls}
                                />
                            </FieldLabel>
                        </div>

                        {error && (
                            <div
                                data-testid="ae-error"
                                className="flex items-start gap-2 border border-brand-red/40 bg-brand-red/10 px-4 py-3 text-sm text-white"
                            >
                                <AlertCircle className="w-4 h-4 text-brand-red mt-0.5 flex-shrink-0" />
                                <span>{error}</span>
                            </div>
                        )}
                    </div>

                    <div className="border-t border-zinc-900 px-6 py-4 flex items-center justify-between gap-3">
                        {mode === "edit" ? (
                            <button
                                type="button"
                                onClick={remove}
                                disabled={deleting || saving}
                                data-testid="ae-delete"
                                className="text-[11px] tracking-[0.2em] uppercase text-zinc-300 hover:text-brand-red transition-colors inline-flex items-center gap-1.5 disabled:opacity-40"
                            >
                                {deleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                                Remove athlete
                            </button>
                        ) : (
                            <span />
                        )}
                        <div className="flex items-center gap-2">
                            <button
                                type="button"
                                onClick={onClose}
                                disabled={saving || deleting}
                                className="btn-ghost-tactical disabled:opacity-50"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={save}
                                disabled={saving || deleting}
                                data-testid="ae-save"
                                className="btn-tactical disabled:opacity-50"
                            >
                                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                                {saving ? "Saving…" : mode === "add" ? "Add athlete" : "Save changes"}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

const inputCls =
    "w-full bg-black border border-zinc-800 px-4 py-3 text-sm text-white placeholder:text-zinc-200 focus:outline-none focus:border-brand-red transition-colors";

function FieldLabel({ label, required, children, span = 1 }) {
    return (
        <div className={span === 2 ? "sm:col-span-2" : ""}>
            <label className="text-[10px] tracking-[0.25em] uppercase text-zinc-300 block mb-2">
                {label}
                {required && <span className="text-brand-red ml-1">*</span>}
            </label>
            {children}
        </div>
    );
}

function Input({ value, onChange, type = "text", testid, placeholder }) {
    return (
        <input
            type={type}
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            data-testid={testid}
            className={inputCls}
        />
    );
}
